import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import { getAmrColumns } from '../../amrPipeline/common-columns';
import { Table } from '../../bidding/common/table';
import { AppState } from '../../../types/state/AppState';
import { Portfolio } from '../../../types/portfolio/Portfolio';
import { TableColumnStickType } from '../../bidding/common/table/types/TableColumnStickType';
import { PortfolioSecurity } from '../../../types/portfolio/PortfolioSecurity';
import { constants } from '../../../constants/constants';
import { pipelineColumnLabels, pipelineTooltipText } from '../../../constants/amr-pipeline/pipelineColumnLabels';
import { roles } from '../../../constants/roles';
import { routes } from '../../../constants/routes';
import { BwicStatusLabel } from '../../common/BwicStatusLabel';
import { EmptyPlaceholder } from '../../common/EmptyPlaceholder';
import { OnHoverTooltip } from '../../common/OnHoverTooltip';
import { portfolioActions } from '../../../actions/portfolio.actions';
import { useActiveInventorySecuritesIds } from '../../../effects/useActiveInventorySecuritesIds';
import { history } from '../../../history';
import { Confirm } from '../../alerts';
import IconSVG from '../../../styles/svg-icons';
import { PortfolioOptions } from '../PortfolioOptions';
import { IColumnDefinition } from '../../bidding/common/table/types/ColumnDefinition';
import { SubscriptionFeature } from '../../../types/billing/SubscriptionFeature';
import { ColumnBuilder } from '../../bidding/common/table/columns/column-builder/ColumnBuilder';
import { user } from '../../../user/user';
import { StickyTableRow } from '../../bidding/common/table/sticky/StickyTableRow';
import { SecurityInventoryLink } from '../../inventory/SecurityInventoryLink';
import { PortfolioLastTraded } from './PortfolioLastTraded';
import { moneyUtils } from '../../../utils/money.utils';
import { PortfolioSecurityTicker } from './PortfolioSecurityTicker';
import { BwicProcessType } from '../../../types/models/Process';
import { IntexLink } from '../../common/IntexLink';
import { PortfolioAgreementDisclaimerActionBlocker } from '../../portfolio-agreement-disclaimer/PortfolioAgreementDisclaimerActionBlocker';
import { CreatePortfolioButton } from '../../portfolio-agreement-disclaimer/CreatePortfolioButton';
import { BloombergLink } from '../../common/BloombergLink';
import { SecurityBwicStatistics } from '../../../types/security/SecurityBwicStatistics';
import { PipelineColumn } from "../../amrPipeline/types/PipelineColumn";
import { ValitanaLink } from '../../common/ValitanaLink';
import { collapseStickRightColumn } from '../../bidding/common/table/CollapseStickRightColumn';
import { createColumnListBuilder } from '../../../utils/ColumnListBuilder';
import { PageConfig } from '../../../types/page-config/PageConfig';
import { ColumnConfigKey } from '../../../types/page-config/column-customizer/ColumnConfigKey';
import { MyBwicIcon } from '../../common/MyBwicIcon';
import { ImDealLookupLink } from '../../amrPipeline/common/ImDealLookupLink';
import { PortfolioSelectAllCheckbox } from './PortfolioSelectAllCheckbox';
import { SinglePortfolioAlertPreferences } from '../portfolio-alert/SinglePortfolioAlertPreferences';
import { bwicColumnsLabels } from '../../../constants/bwicColumnsLabels';
import { useAppDispatch } from '../../../effects/useAppDispatch';
import { SecurityLastTradedDate } from '../../common/SecurityLastTradedDate';
import { ViewDealDocumentsButton } from '../../documents/deal-documents-popup/ViewDealDocumentButton';
import { InfoTooltip } from '../../common/InfoTooltip';
import { PortfolioTransactionDetailsLink } from './PortfolioTransactionDetailsLink';
import { PortfolioArrangersPipelineLink } from './PortfolioArrangerPipelineLink';
import { PortfolioRollerDeadline } from './PortfolioRollerDealine';
import { BwicMonitorLookupLink } from '../../common/BwicMonitorLookupLink';
import { amrFormatUtils, dateTimeUtils, formatUtils, numericUtils } from '../../../utils';
import { CleansingNoticeButton } from '../../documents/deal-documents-popup/CleansingNoticeButton';
import { PeriodEndTooltip } from '../../common/PeriodEnd';
import { BwicStatus } from '../../../types/enums/BwicStatus';
import { ValueBlocker } from '../../access/ValueBlocker';
import { DmWalCurveButton } from '../../dm-wal-curve/DmWalCurveButton';
import { Wal } from '../../dm-wal-curve/Wal';

export interface ParsedHistory {
    currentBwic?: SecurityBwicStatistics,
    bwicHistory: SecurityBwicStatistics[],
}

interface TableContext {
    parsedHistory: ParsedHistory
}

export interface Props {
    filteredPortfolios: Portfolio[],
    pageConfig?: PageConfig,
}

export const parseSecurityHistory = (history: SecurityBwicStatistics[]): ParsedHistory => {
    const currentBwic = [...history]
        .sort((a, b) => new Date(a.bidsDueUtc).getTime() - new Date(b.bidsDueUtc).getTime())
        .find(h => h.status === BwicStatus.scheduled || h.status === BwicStatus.bidding);
    const bwicHistory = [...history]
        .sort((a, b) => new Date(b.bidsDueUtc).getTime() - new Date(a.bidsDueUtc).getTime())
        .filter(h => h.status === BwicStatus.finished);
    return { currentBwic, bwicHistory };
};

export const PortfolioTable = ({ filteredPortfolios, pageConfig }: Props) => {
    const isBroker = user.hasRoles(...roles.bd());
    const columnsConfig = user.hasFeatures(SubscriptionFeature.PortfolioColumnCustomizer)
        ? pageConfig?.columns
        : undefined;

    const dispatch = useAppDispatch();

    const portfolios = useSelector((state: AppState) => state.entities.portfolios.items)
    const selectedState = useSelector((state: AppState) => state.portfolio.selectedState);
    const portfolioDelete = useSelector((state: AppState) => state.portfolio.portfolioDelete);
    const securityDetails = useSelector((s: AppState) => s.securityDetails);
    const selectedSecurity = useSelector((s: AppState) => s.portfolio.selectedSecurity);


    const [, activeInventorySecurityIdByCount] = useActiveInventorySecuritesIds();

    const renderPortfolioSecurities = useCallback((portfolio: Portfolio, columnType?: TableColumnStickType) => {
        const portfolioSelectedSecurities = selectedState[portfolio.id] ? selectedState[portfolio.id].securitiesSelectedState : {};

        const handleSecuritySelect = (securityId: number, selected: boolean) =>
            dispatch(portfolioActions.portfolioSecuritySelectionChange(portfolio.id, securityId, !selected))

        const getPositionListColumns = (
            selectedState: { [securityId: number]: boolean },
            activeInventorySecurityIdByCount: { [securityId: number]: number },
        ): ColumnBuilder<PortfolioSecurity>[][] => {
            const { dealName, collateralType, manager, nonCallPeriodEnd, reinvestmentPeriodEnd, maturity, vintage, trustee, amrDeal, staticDeal, euCompliance, esg, closingDate, outOfNC, outOfRI, nonCallPeriodEndYears, reinvestmentPeriodEndYears } = getAmrColumns();
            const leftColumns: IColumnDefinition<PortfolioSecurity>[] = [{
                columnKey: 'ticker',
                configKey: ColumnConfigKey.Ticker,
                renderColumnHeaderContent: () => '',
                renderColumnContent: (security, context) => (
                    <PortfolioSecurityTicker
                        isSecuritySelected={selectedState[security.id]}
                        security={security}
                        onSecuritySelect={() => handleSecuritySelect(security.id, selectedState[security.id])}
                        currentBwic={context.parsedHistory.currentBwic}
                        lastBwic={context.parsedHistory.bwicHistory?.[0]}
                    />
                ),
                className: 'data-list-cell-lg-02 padding-l-0',
            }];

            const middleColumns: IColumnDefinition<PortfolioSecurity, TableContext>[] = [{
                columnKey: 'deal-documents',
                configKey: ColumnConfigKey.DealDocuments,
                renderColumnHeaderContent: () => 'Docs',
                renderColumnContent: security => security.documentsCount
                    ? <ViewDealDocumentsButton className="text-regular" security={security} />
                    : constants.emptyPlaceholder,
                className: 'data-list-cell-xs',
            }, {
                columnKey: 'currency',
                configKey: ColumnConfigKey.Currency,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => security.currency,
                className: '',
            }, {
                columnKey: 'rtg',
                configKey: ColumnConfigKey.Rating,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => security.rating,
                className: 'data-list-cell-xs',
            }, {
                columnKey: 'size',
                configKey: ColumnConfigKey.Size,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => moneyUtils.amountToMM(security.size),
                className: 'data-list-cell-xs text-right',
            }, {
                columnKey: 'is-my-bwic',
                configKey: ColumnConfigKey.CurrentBwic,
                renderColumnHeaderContent: () => '',
                renderColumnContent: (security, context) => context.parsedHistory.currentBwic ? (
                    <MyBwicIcon bwic={context.parsedHistory.currentBwic} />
                ) : null,
                className: 'data-list-cell-xxs',
            }, {
                columnKey: 'currentBWIC',
                configKey: ColumnConfigKey.CurrentBwic,
                renderColumnHeaderContent: () => '',
                renderColumnContent: (security, context) =>
                    context.parsedHistory.currentBwic
                        ? (
                            <div className="flex-row">
                                <BwicStatusLabel
                                    status={context.parsedHistory.currentBwic.status}
                                    liveBidding={context.parsedHistory.currentBwic.process?.type === BwicProcessType.Live}
                                    isParsed={context.parsedHistory.currentBwic.isParsed}
                                    directBidding={context.parsedHistory.currentBwic.isClearingBankParticipant}
                                    isAllToAll={context.parsedHistory.currentBwic.isAllToAll}
                                    bwicTypeIconVisible={false}
                                />
                                <div className="margin-l-8">
                                    <BwicMonitorLookupLink ticker={security.ticker}
                                        text={dateTimeUtils.utcToLocalString(context.parsedHistory.currentBwic.bidsDueUtc)}
                                    />
                                </div>
                            </div>
                        )
                        : constants.emptyPlaceholder,
                className: 'data-list-cell-xl-01',
            }, {
                columnKey: 'inventory',
                configKey: ColumnConfigKey.Inventory,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security =>
                    !!activeInventorySecurityIdByCount[security.securityId] ? (
                        <SecurityInventoryLink
                            count={activeInventorySecurityIdByCount[security.securityId]}
                            ticker={security.ticker}
                        />
                    ) : (constants.emptyPlaceholder),
                className: 'data-list-cell-sm-02',
            }, {
                columnKey: 'lastTraderIsMine',
                configKey: ColumnConfigKey.LastTraded,
                renderColumnHeaderContent: () => '',
                renderColumnContent: (security, context) => (
                    <SecurityLastTradedDate inventoryLastTraded={security.inventoryLastTradeDate} bwicLastTradedDate={context.parsedHistory.bwicHistory?.[0]?.bidsDueUtc}>
                        {(isInventoryDate) => context.parsedHistory.bwicHistory?.[0] && !isInventoryDate ? <MyBwicIcon bwic={context.parsedHistory.bwicHistory[0]} /> : null}
                    </SecurityLastTradedDate>
                ),
                className: 'data-list-cell-xxs',
            }, {
                columnKey: 'dueDate',
                configKey: ColumnConfigKey.LastTraded,
                renderColumnHeaderContent: () => '',
                renderColumnContent: (security, context) => (
                    <SecurityLastTradedDate inventoryLastTraded={security.inventoryLastTradeDate} bwicLastTradedDate={context.parsedHistory.bwicHistory?.[0]?.bidsDueUtc}>
                        {(isInventoryDate, lastTradeDate) => lastTradeDate ? moment(lastTradeDate).format(constants.dateFormat) : constants.emptyPlaceholder}
                    </SecurityLastTradedDate>
                ),
                className: 'data-list-cell-sm-02',
            }, {
                columnKey: 'lastTraded',
                configKey: ColumnConfigKey.LastTraded,
                renderColumnHeaderContent: () => '',
                renderColumnContent: (security, context) =>
                    <SecurityLastTradedDate inventoryLastTraded={security.inventoryLastTradeDate} bwicLastTradedDate={context.parsedHistory.bwicHistory?.[0]?.bidsDueUtc}>
                        {isInventoryDate => isInventoryDate ? null : <PortfolioLastTraded bwicHistory={context.parsedHistory.bwicHistory} />}
                    </SecurityLastTradedDate>,
                className: 'data-list-cell-sm',
            }, {
                columnKey: 'colorDm',
                configKey: ColumnConfigKey.ColorDm,
                renderColumnHeaderContent: () => 'Color DM',
                renderColumnContent: (security, context) => {
                    const bwic = context.parsedHistory.currentBwic ?? context.parsedHistory.bwicHistory?.[0];

                    return (
                        <ValueBlocker feature={SubscriptionFeature.CanViewDmWalCurveFromPortfolio}>
                            {numericUtils.isNumber(bwic?.colorDm) ? (
                                <DmWalCurveButton
                                    position={security}
                                    bwicDate={bwic.bidsDueUtc}
                                    onClick={isActive => isActive
                                        ? dispatch(portfolioActions.showBwicDetails(security, context.parsedHistory.currentBwic, bwic))
                                        : dispatch(portfolioActions.resetRowSelection())}
                                >
                                    {formatUtils.formatDiscountMargin(bwic.colorDm)}
                                </DmWalCurveButton>
                            ) : constants.emptyPlaceholder}
                        </ValueBlocker>);
                },
                className: 'text-right'
            }, {
                columnKey: 'wal',
                configKey: ColumnConfigKey.ColorWal,
                renderColumnHeaderContent: () => bwicColumnsLabels[ColumnConfigKey.ColorWal],
                renderColumnContent: (security, context) => {
                    const bwic = context.parsedHistory.currentBwic ?? context.parsedHistory.bwicHistory?.[0];

                    return (
                        <ValueBlocker feature={SubscriptionFeature.CanViewDmWalCurveFromPortfolio}>
                            {numericUtils.isNumber(bwic?.colorWal) ||
                                numericUtils.isNumber(bwic?.evalPriceTalkWal) ||
                                bwic?.pxTalks?.some(p => numericUtils.isNumber(p.wal)) ? (
                                <DmWalCurveButton
                                    position={security}
                                    bwicDate={bwic.bidsDueUtc}
                                    onClick={isActive => isActive
                                        ? dispatch(portfolioActions.showBwicDetails(security, context.parsedHistory.currentBwic, bwic))
                                        : dispatch(portfolioActions.resetRowSelection())}
                                >
                                    <Wal
                                        colorWal={bwic.colorWal}
                                        evalPriceTalkWal={bwic.evalPriceTalkWal}
                                        priceTalks={bwic.pxTalks}
                                    />
                                </DmWalCurveButton>) : constants.emptyPlaceholder}
                        </ValueBlocker>
                    )
                },
                className: 'data-list-cell-xxs-01 text-right'
            }, {
                columnKey: 'cleansing-notice-document',
                configKey: ColumnConfigKey.CleansingNotice,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security =>
                    <CleansingNoticeButton className="text-regular" security={security} />,
                className: 'data-list-cell-sm',
            }, {
                columnKey: 'im-transaction',
                configKey: ColumnConfigKey.ImTransaction,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => <PortfolioTransactionDetailsLink security={security} />,
                className: 'data-list-cell-lg',
            }, {
                columnKey: 'roller-deadline',
                configKey: ColumnConfigKey.RollerDeadline,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => <PortfolioRollerDeadline security={security} />,
                className: 'data-list-cell-lg-02',
            }, {
                columnKey: 'arranger-pipeline',
                configKey: ColumnConfigKey.ArrangerPipeline,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => <PortfolioArrangersPipelineLink security={security} />,
                className: 'data-list-cell-md',
            },
                dealName,
                manager,
                closingDate,
                nonCallPeriodEnd,
                outOfNC,
                { ...nonCallPeriodEndYears, className: "data-list-cell-sm-01 text-right" },
                reinvestmentPeriodEnd,
                { ...outOfRI, className: "data-list-cell-sm-02" },
                { ...reinvestmentPeriodEndYears, className: "data-list-cell-sm-01 text-right" },
                maturity,
                vintage, {
                columnKey: 'coupon',
                configKey: ColumnConfigKey.CurrentCoupon,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security =>
                    amrFormatUtils.formatInventoryCoupon(security.floaterIndex, security.margin, constants.emptyPlaceholder),
                className: 'data-list-cell-md',
            },
                trustee,
                amrDeal,
                staticDeal,
                euCompliance,
                esg,
                collateralType
            ];

            const imDealLookup: IColumnDefinition<PortfolioSecurity> = {
                columnKey: 'imDealLookup',
                renderColumnHeaderContent: () => null,
                renderColumnContent: security =>
                    <>
                        {
                            !!security.dealReferenceName &&
                            !!security.dealLegalName &&
                            <ImDealLookupLink
                                dealReferenceName={security.dealReferenceName}
                                dealLegalName={security.dealLegalName}
                            />
                        }
                    </>,
                className: 'data-list-cell-xxs margin-l-a padding-r-0 text-right'
            };
            const intex: IColumnDefinition<PortfolioSecurity> = {
                columnKey: 'intex',
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => <IntexLink positionId={security.ticker} />,
                className: 'data-list-cell-xxs text-right padding-r-0',
            };
            const bloomberg: IColumnDefinition<PortfolioSecurity> = {
                columnKey: 'bloomberg',
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => <BloombergLink identifier={security.ticker} />,
                className: 'data-list-cell-xxs text-right padding-r-0',
            };
            const valitana: IColumnDefinition<PortfolioSecurity> = {
                columnKey: 'valitana',
                configKey: ColumnConfigKey.Actions,
                renderColumnHeaderContent: () => '',
                renderColumnContent: security => <ValitanaLink identifier={security.ticker} />,
                className: 'data-list-cell-xxs text-right',
            };

            const left = createColumnListBuilder<PortfolioSecurity>(columnsConfig)
                .add(...leftColumns)
                .result()
                .map(c => new ColumnBuilder(c));
            const middle = createColumnListBuilder<PortfolioSecurity>(columnsConfig)
                .add(...middleColumns)
                .result()
                .map(c => new ColumnBuilder(c));
            const right = createColumnListBuilder<PortfolioSecurity>(columnsConfig)
                .add(collapseStickRightColumn, imDealLookup, intex, bloomberg, valitana)
                .result()
                .map(c => new ColumnBuilder(c));

            return [left, middle, right];
        }

        const [left, middle, right] = getPositionListColumns(
            portfolioSelectedSecurities,
            activeInventorySecurityIdByCount
        );

        let columns: ColumnBuilder<PortfolioSecurity>[] = [];

        if (columnType === TableColumnStickType.Left) columns = left;
        if (columnType === TableColumnStickType.Middle) columns = middle;
        if (columnType === TableColumnStickType.Right) columns = right;

        return portfolio.securities.map(s => (
            <StickyTableRow
                key={s.id}
                rowKey={String(s.id)}
                columns={columns}
                dataItem={s}
                className={classNames({ active: s.id === selectedSecurity?.id })}
                context={{ parsedHistory: parseSecurityHistory(s.history) }}
            />
        ));
    }, [selectedState, activeInventorySecurityIdByCount, selectedSecurity, dispatch, columnsConfig])

    const getPortfolioTableColumns = useMemo(() => {
        const ticker = {
            columnKey: 'ticker',
            configKey: ColumnConfigKey.Ticker,
            renderColumnHeaderContent: () => 'Ticker',
            renderColumnContent: (portfolio: Portfolio) =>
                <PortfolioSelectAllCheckbox
                    portfolio={portfolio}
                    selectedPortfolioState={selectedState[portfolio.id]}
                />,
            headerClassName: 'data-list-cell-lg-02 padding-l-0',
            bodyClassName: 'data-list-cell-portfolio-ticker padding-l-0',
            stickLeft: true
        };
        const inventory = {
            columnKey: 'inventory',
            configKey: ColumnConfigKey.Inventory,
            renderColumnHeaderContent: () => (
                <>
                    Inventory
                    <OnHoverTooltip
                        overlay={`Click on ‘View’ to see this security in the ${isBroker ? '' : 'Dealer'} Inventory`}
                    >
                        <IconSVG name="info" width={16} height={16} />
                    </OnHoverTooltip>
                </>
            ),
            renderColumnContent: () => '',
            className: 'data-list-cell-sm-02',
        }
        const imTransaction = {
            columnKey: 'im-transaction',
            configKey: ColumnConfigKey.ImTransaction,
            renderColumnHeaderContent: () =>
                <>Issuance Monitor <InfoTooltip text="Click on the transaction type to see the latest (Active or Priced) transaction of the deal with the involving security on the Issuance Monitor" /></>,
            renderColumnContent: () => '',
            className: 'data-list-cell-lg',
        }
        const rollerDeadline = {
            columnKey: 'roller-deadline',
            configKey: ColumnConfigKey.RollerDeadline,
            renderColumnHeaderContent: () => "Roller Deadline (EST)",
            renderColumnContent: () => '',
            className: 'data-list-cell-lg-02',
        }
        const arrangerPipeline = {
            columnKey: 'arranger-pipeline',
            configKey: ColumnConfigKey.ArrangerPipeline,
            renderColumnHeaderContent: () =>
                <>Arranger Pipeline <InfoTooltip text="Click on ‘View’ to see the deal with the involving security on the Arranger Pipeline" /></>,
            renderColumnContent: () => '',
            className: 'data-list-cell-md',
        }
        const rtg = {
            columnKey: 'rtg',
            configKey: ColumnConfigKey.Rating,
            renderColumnHeaderContent: () => 'Rtg',
            renderColumnContent: () => '',
            className: 'data-list-cell-xs',
        }
        const size = {
            columnKey: 'size',
            configKey: ColumnConfigKey.Size,
            renderColumnHeaderContent: () => 'Size, MM',
            renderColumnContent: () => '',
            className: 'data-list-cell-xs text-right',
        }
        const currentBWICmyBWIC = {
            columnKey: 'is-my-bwic',
            configKey: ColumnConfigKey.CurrentBwic,
            renderColumnHeaderContent: () => '',
            renderColumnContent: () => '',
            className: 'data-list-cell-xxs',
        }
        const currentBWIC = {
            columnKey: 'currentBWIC',
            configKey: ColumnConfigKey.CurrentBwic,
            renderColumnHeaderContent: () => 'Current BWIC',
            renderColumnContent: () => '',
            className: 'data-list-cell-xl-01',
        }
        const lastTraderIsMine = {
            columnKey: 'lastTraderIsMine',
            configKey: ColumnConfigKey.LastTraded,
            renderColumnHeaderContent: () => '',
            renderColumnContent: () => '',
            className: 'data-list-cell-xxs',
        }
        const lastTraded = {
            columnKey: 'lastTraded',
            configKey: ColumnConfigKey.LastTraded,
            renderColumnHeaderContent: () => 'Last Traded',
            renderColumnContent: () => '',
            className: 'data-list-cell-sm-02',
        }
        const lastTradedStatus = {
            columnKey: 'lastTradedStatus',
            configKey: ColumnConfigKey.LastTraded,
            renderColumnHeaderContent: () => '',
            renderColumnContent: () => '',
            className: 'data-list-cell-sm',
        };
        const colorDm = {
            columnKey: 'colorDm',
            configKey: ColumnConfigKey.ColorDm,
            renderColumnHeaderContent: () => 'Color DM',
            renderColumnContent: () => '',
            className: 'text-right'
        };
        const colorWal = {
            columnKey: 'wal',
            configKey: ColumnConfigKey.ColorWal,
            renderColumnHeaderContent: () => bwicColumnsLabels[ColumnConfigKey.ColorWal],
            renderColumnContent: () => '',
            className: 'data-list-cell-xxs-01 text-right'
        };
        const cleansingNotice = {
            columnKey: 'cleansing-notice-document',
            configKey: ColumnConfigKey.CleansingNotice,
            renderColumnHeaderContent: () => 'Cleansing Notice',
            renderColumnContent: () => '',
            className: 'data-list-cell-sm',
        }
        const dealName = {
            columnKey: 'dealName',
            configKey: ColumnConfigKey.DealName,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.dealName],
            renderColumnContent: () => '',
            className: 'data-list-cell-lg',
        }
        const manager = {
            columnKey: 'manager',
            configKey: ColumnConfigKey.CollateralManager,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.manager],
            renderColumnContent: () => '',
            className: 'data-list-cell-lg',
        }
        const closingDate = {
            columnKey: 'closingDate',
            configKey: ColumnConfigKey.ClosingDate,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.closingDate],
            renderColumnContent: () => '',
            className: '',
        }
        const ncEnd = {
            columnKey: 'NcEnd',
            configKey: ColumnConfigKey.NcEnd,
            renderColumnHeaderContent: () => bwicColumnsLabels[ColumnConfigKey.NcEnd],
            renderColumnContent: () => '',
            className: "data-list-cell-sm-02"
        }
        const riEnd = {
            columnKey: 'RiEnd',
            configKey: ColumnConfigKey.RiEnd,
            renderColumnHeaderContent: () => bwicColumnsLabels[ColumnConfigKey.RiEnd],
            renderColumnContent: () => '',
            className: "data-list-cell-sm-02"
        }
        const ncEndYrs = {
            columnKey: 'ncEndYrs',
            configKey: ColumnConfigKey.NcEndYrs,
            renderColumnHeaderContent: () => (
                <OnHoverTooltip overlay={pipelineTooltipText[PipelineColumn.nonCallPeriodEnd]}>
                    {pipelineColumnLabels[PipelineColumn.nonCallPeriodEndYears]}
                    <IconSVG name="info" width={16} height={16} />
                </OnHoverTooltip>
            ),
            renderColumnContent: () => '',
            className: "data-list-cell-sm-01 text-right"
        }
        const outOfNcEnd = {
            columnKey: 'outOfNcEnd',
            configKey: ColumnConfigKey.OutOfNc,
            renderColumnHeaderContent: () =>
                <PeriodEndTooltip mode="nonCall" title={pipelineColumnLabels[PipelineColumn.outOfNC] ?? ''} />,
            renderColumnContent: () => '',
            className: "data-list-cell-sm-02"
        }
        const riEndYrs = {
            columnKey: 'riEndYrs',
            configKey: ColumnConfigKey.RiEndYrs,
            renderColumnHeaderContent: () => (
                <OnHoverTooltip overlay={pipelineTooltipText[PipelineColumn.reinvestmentPeriodEnd]}>
                    {pipelineColumnLabels[PipelineColumn.reinvestmentPeriodEndYears]}
                    <IconSVG name="info" width={16} height={16} />
                </OnHoverTooltip>
            ),
            renderColumnContent: () => '',
            className: "data-list-cell-sm-01 text-right"
        }
        const outOfRiEnd = {
            columnKey: 'outOfRiEnd',
            configKey: ColumnConfigKey.OutOfRi,
            renderColumnHeaderContent: () =>
                <PeriodEndTooltip mode="reinvestment" title={pipelineColumnLabels[PipelineColumn.outOfRI] ?? ''} />,
            renderColumnContent: () => '',
            className: "data-list-cell-sm-02"
        }
        const maturity = {
            columnKey: 'maturity',
            configKey: ColumnConfigKey.Maturity,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.maturity],
            renderColumnContent: () => '',
            className: ''
        }
        const vintage = {
            columnKey: 'vintage',
            configKey: ColumnConfigKey.Vintage,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.vintage],
            renderColumnContent: () => '',
            className: "data-list-cell-xs-sm"
        }
        const coupon = {
            columnKey: 'coupon',
            configKey: ColumnConfigKey.CurrentCoupon,
            renderColumnHeaderContent: () => bwicColumnsLabels[ColumnConfigKey.CurrentCoupon],
            renderColumnContent: () => '',
            className: 'data-list-cell-md',
        }
        const trustee = {
            columnKey: 'trustee',
            configKey: ColumnConfigKey.Trustee,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.trustee],
            renderColumnContent: () => '',
            className: 'data-list-cell-lg',
        }
        const amrDeal = {
            columnKey: 'amrDeal',
            configKey: ColumnConfigKey.AmrDeal,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.amrDeal],
            renderColumnContent: () => '',
            className: 'data-list-cell-sm',
        }
        const staticDeal = {
            columnKey: 'staticDeal',
            configKey: ColumnConfigKey.StaticDeal,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.staticDeal],
            renderColumnContent: () => '',
            className: 'data-list-cell-sm-02',
        }
        const euComp = {
            columnKey: 'euComp',
            configKey: ColumnConfigKey.EuCompliance,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.euCompliance],
            renderColumnContent: () => '',
        }
        const esg = {
            columnKey: 'esg',
            configKey: ColumnConfigKey.Esg,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.esg],
            renderColumnContent: () => '',
            className: 'data-list-cell-xs',
        }
        const collat = {
            columnKey: 'collat',
            configKey: ColumnConfigKey.CollateralType,
            renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.collateralType],
            renderColumnContent: () => '',
            className: 'data-list-cell-xs',
        }
        const dealDocs = {
            columnKey: 'deal-documents',
            configKey: ColumnConfigKey.DealDocuments,
            renderColumnHeaderContent: () => 'Docs',
            renderColumnContent: () => '',
            className: 'data-list-cell-xs',
        }
        const currency = {
            columnKey: 'currency',
            configKey: ColumnConfigKey.Currency,
            renderColumnHeaderContent: () => 'Ccy',
            renderColumnContent: () => '',
            className: '',
        }
        const imDealLookupPlaceholder = {
            columnKey: 'imDealLookupPlaceholder',
            renderColumnHeaderContent: () => '',
            renderColumnContent: () => '',
            className: 'data-list-cell-xxs',
            stickRight: true
        }
        const options = {
            columnKey: 'options',
            renderColumnHeaderContent: () => '',
            renderColumnContent: (portfolio: Portfolio) => (
                <div className="flex-row">
                    <div className="flex-item-right flex-row">
                        <div className="portfolio-alert-selector">
                            <SinglePortfolioAlertPreferences portfolio={portfolio} />
                        </div>
                        <PortfolioOptions portfolio={portfolio} />
                    </div>
                </div>
            ),
            headerClassName: 'data-list-cell-sm text-right',
            bodyClassName: 'data-list-cell-sm text-right overflow-visible',
            stickRight: true
        }

        const columnDefinitions: IColumnDefinition<Portfolio>[] =
            createColumnListBuilder<Portfolio>(columnsConfig)
                .add(ticker)
                .add(dealDocs)
                .add(currency)
                .add(rtg)
                .add(size)
                .add(currentBWICmyBWIC, currentBWIC)
                .add(inventory)
                .add(lastTraderIsMine, lastTraded, lastTradedStatus, colorDm, colorWal)
                .add(cleansingNotice)
                .add(imTransaction)
                .add(rollerDeadline)
                .add(arrangerPipeline)
                .add(dealName)
                .add(manager)
                .add(closingDate)
                .add(ncEnd)
                .add(outOfNcEnd)
                .add(ncEndYrs)
                .add(riEnd)
                .add(outOfRiEnd)
                .add(riEndYrs)
                .add(maturity)
                .add(vintage)
                .add(coupon)
                .add(trustee)
                .add(amrDeal)
                .add(staticDeal)
                .add(euComp)
                .add(esg)
                .add(collat)
                .add(collapseStickRightColumn)
                .add(imDealLookupPlaceholder)
                .add(options)
                .result()

        return columnDefinitions.map(c => new ColumnBuilder(c));
        // eslint-disable-next-line
    }, [selectedState, isBroker, securityDetails, pageConfig?.columns])

    if (!portfolios) {
        return null;
    }

    if (!portfolios.length) {
        const handleClick = () => history.push(routes.newPortfolio);

        return (
            <EmptyPlaceholder
                title="No Portfolio Created"
                text={
                    <>
                        You have not created any portfolios yet.
                        <br />
                        To create a portfolio, click "Add Portfolio" below.
                    </>
                }
            >
                <PortfolioAgreementDisclaimerActionBlocker onConfirm={handleClick}>
                    <CreatePortfolioButton className="btn btn-main" onClick={handleClick}>
                        Add portfolio
                    </CreatePortfolioButton>
                </PortfolioAgreementDisclaimerActionBlocker>
            </EmptyPlaceholder>
        )
    }

    return (
        <>
            <Table
                dataItems={filteredPortfolios}
                columns={getPortfolioTableColumns}
                renderCollapsibleItem={renderPortfolioSecurities}
                collapsible={true}
                sticky={true}
                defaultExpandAll={true}
                isRightColumnCollapsible={true}
            />
            {
                portfolioDelete.visible &&
                <Confirm
                    text={`Please confirm you would like to delete ${portfolioDelete.portfolio.name}.`}
                    onCancel={() => dispatch(portfolioActions.deletePortfolioConfirmation(false))}
                    onConfirm={() => dispatch(portfolioActions.deletePortfolio(portfolioDelete.portfolio.id))}
                />
            }
        </>
    )
}
